import { memo, useMemo } from "react";
import { StyleSheet } from "react-native-unistyles";
import { View, Text } from "@src/components";
import SegmentedSelector from "@src/components/SegmentedSelector";
import { capitalize } from "@src/utils/capitalize";
import { isWeb } from "@src/utils/web-helpers";

export type SiteRole = "manager" | "viewer";

export type SiteWithCameras = {
  siteId: string;
  siteName: string;
  cameraNames: string[];
};

interface AssignSiteRolesListProps {
  sites: SiteWithCameras[];
  roleMap: Map<string, SiteRole>;
  onRoleChange: (siteId: string, role: SiteRole) => void;
}

export const AssignSiteRolesList = memo(({ sites, roleMap, onRoleChange }: AssignSiteRolesListProps) => {
  const options = useMemo(() => [{ title: "Manager" }, { title: "Viewer" }], []);

  return (
    <View style={styles.card}>
      {sites.map((site, index) => {
        const isLast = index === sites.length - 1;
        const selectedRole = roleMap.get(site.siteId) ?? "viewer";

        return (
          <View
            key={site.siteId}
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            style={[styles.siteRow, !isLast && styles.siteRowBorder]}
          >
            <View gap="xs" flex={1}>
              <Text textStyle="sm/medium">{site.siteName}</Text>
              {site.cameraNames.map((name) => (
                <Text key={name} textStyle="xxs/regular" color="textMuted">
                  {name}
                </Text>
              ))}
            </View>
            <SegmentedSelector
              optionsLabels={options}
              selectedOption={capitalize(selectedRole)}
              onSelectOption={(option) => onRoleChange(site.siteId, option.toLowerCase() as SiteRole)}
              containerStyle={styles.roleSelectorContainer}
              activeBoxStyle={styles.roleSelectorActiveBox}
              width={isWeb ? 200 : undefined}
            />
          </View>
        );
      })}
    </View>
  );
});

const styles = StyleSheet.create((theme) => ({
  card: {
    borderRadius: theme.spacing.lg,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    overflow: "hidden",
  },
  siteRow: {
    paddingVertical: theme.spacing["3xl"],
    paddingHorizontal: theme.spacing["3xl"],
  },
  siteRowBorder: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.borderCard,
  },
  roleSelectorContainer: {
    backgroundColor: "transparent",
  },
  roleSelectorActiveBox: {
    backgroundColor: theme.colors.surface2.hover,
  },
}));
